// @ts-nocheck
import { cancelReasonLabel } from "@kawkaw/shared";
import { useState, useEffect } from "react";
import type React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Button, Drawer, Descriptions, Typography, Divider, App, Dropdown, Table, Select } from "../components/admin-ui";
import { CarOutlined, ShoppingOutlined, EllipsisOutlined, PhoneOutlined, CheckOutlined, DownloadOutlined } from "../components/admin-icons";
import { OrderCancelModal } from "../components/OrderCancelModal";
import { StatusBadge } from "../components/StatusBadge";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { Order, OrderItem } from "@kawkaw/shared";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
import { formatMytStamp, formatOrderDate, formatOrderTime } from "../utils/formatLocaleDate";
import { useElementHeight } from "../hooks/useElementHeight";
import dayjs from "dayjs";
const { color: T } = tokens;
import { useAdminOrders, useAdminOrder, useAdminCancelOrder, useAdminUpdateOrderStatus, useDashboardStats, useAdminStalls, fetchAdminOrdersForExport } from "../api/hooks";
import {
  CANCEL_HIDDEN_ORDER_STATUSES,
  ORDER_ACTION_BUTTON_CLASS,
  ORDER_ACTION_LABEL_KEY,
  READY_OR_LATER_ORDER_STATUSES,
  TERMINAL_ORDER_STATUSES,
  getOrderCustomerDisplay,
  getOrderStatusAction,
  lalamoveStatusKey,
  normalizeOrderStatus,
} from "../utils/orderUi";

const { Text } = Typography;

const ORDERS_PAGE_CLASS = [
  "bg-[#f8f9fa] px-8 pb-6 pt-7",
  "[&_table]:border-separate [&_table]:border-spacing-0 [&_table]:bg-transparent",
  "[&_th]:h-12 [&_th]:border-b-0 [&_th]:bg-[rgba(243,244,245,0.5)] [&_th]:px-6 [&_th]:py-4 [&_th]:text-xs [&_th]:font-extrabold [&_th]:uppercase [&_th]:tracking-[0.6px] [&_th]:text-[#534434]",
  "[&_td]:min-h-[88px] [&_td]:border-b [&_td]:border-[#f8fafc] [&_td]:bg-transparent [&_td]:px-6 [&_td]:py-8 [&_td]:align-middle [&_td]:text-[#191c1d]",
  "[&_tr:hover_td]:bg-transparent [&_tbody_tr]:cursor-default [&_tbody_td]:pointer-events-none [&_tbody_td:last-child]:pointer-events-auto",
  "[&_th:first-child]:pl-6 [&_td:first-child]:pl-6 [&_th:last-child]:pr-12 [&_td:last-child]:pr-12 [&_th:last-child]:text-left [&_td:last-child]:text-left",
].join(" ");

const ORDERS_FILTER_CLASS = "fixed right-0 top-16 z-40 bg-[#f8f9fa] px-8 pb-4 pt-8 left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5";
const orderRowClass = (index: number) => index % 2 === 0
  ? "[&>td]:!bg-white hover:[&>td]:!bg-white"
  : "[&>td]:!bg-transparent hover:[&>td]:!bg-transparent";

function rm(value: unknown): string {
  return Number(value ?? 0).toFixed(2);
}

function OrderStatusPill({ status }: { status: string }) {
  return <StatusBadge status={status} kind="order" size="xs" className="admin-orders-status admin-data-badge" />;
}

// ── Contextual action button ─────────────────────────────────────────────────
function ActionButton({ order, onAction }: {
  order: Order & { type: string };
  onAction: (id: string, status: string, order?: unknown) => void;
}) {
  const { t } = useTranslation();
  const s = normalizeOrderStatus(order.status);
  const base = "inline-flex h-8 items-center justify-center whitespace-nowrap rounded-lg px-4 text-xs font-extrabold";
  const statusLabel = t(`orders.status.${s}`, { defaultValue: s.replace(/_/g, " ") });
  const action = getOrderStatusAction(order);

  if (action) {
    return (
      <button type="button" className={`admin-orders-action ${base} ${ORDER_ACTION_BUTTON_CLASS[action.actionKey]}`} onClick={() => onAction(order.id, action.nextStatus, order)}>
        {t(ORDER_ACTION_LABEL_KEY[action.actionKey])}
      </button>
    );
  }
  if (["new", "completed", "delivered", "cancelled"].includes(s)) return null;
  return <StatusBadge status={s} kind="order" className={base} label={statusLabel} />;
}

export function OrdersScreen() {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { confirm: confirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = searchParams.get("customerId") ?? undefined;
  const [page, setPage] = useState(1);
  const [statusTab, setStatusTab] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const urlQ = searchParams.get("q");
  useEffect(() => { if (urlQ != null) { setSearch(urlQ); setPage(1); } }, [urlQ]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stallId, setStallId] = useState<string | undefined>(undefined);
  const [source, setSource] = useState<string | undefined>(undefined);
  const [datePreset, setDatePreset] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [filterRef, filterHeight] = useElementHeight<HTMLDivElement>();

  useEffect(() => {
    if (source === "walkin" && typeFilter === "delivery") setTypeFilter("all");
  }, [source, typeFilter]);

  const STATUS_TABS = [
    { key: "all", label: t("orders.filterAll") },
    { key: "new", label: t("orders.statusTabNew") },
    { key: "preparing", label: t("orders.statusTabPreparing") },
    { key: "ready", label: t("orders.statusTabReady") },
    { key: "out_for_delivery", label: t("orders.statusTabOutForDelivery") },
    { key: "delivered", label: t("orders.statusTabDelivered") },
    { key: "completed", label: t("orders.statusTabCompleted") },
    { key: "cancelled", label: t("orders.statusTabCancelled") },
  ];

  const { data: stallsData } = useAdminStalls();
  const stalls = stallsData?.data ?? [];

  const dateRange = (() => {
    const days = datePreset === "7d" ? 7 : datePreset === "30d" ? 30 : datePreset === "90d" ? 90 : null;
    if (!days) return {};
    return {
      from: dayjs().subtract(days, "day").startOf("day").toISOString(),
      to: dayjs().endOf("day").toISOString(),
    };
  })();

  const { data, isLoading } = useAdminOrders({
    page,
    limit: 10,
    status: statusTab !== "all" ? statusTab : undefined,
    type: typeFilter !== "all" ? typeFilter : undefined,
    q: search.trim() || undefined,
    stallId,
    source,
    customerId,
    ...dateRange,
  });

  const { data: orderDetail } = useAdminOrder(selectedId ?? "");
  const cancelOrder = useAdminCancelOrder();
  const updateStatus = useAdminUpdateOrderStatus();
  const { data: statsData } = useDashboardStats();
  const stats = statsData?.data;
  const activeOrders = stats?.activeNow ?? 0; // every in-flight order incl. brand-new ones

  const [cancelTarget, setCancelTarget] = useState<Order | null>(null);

  const onCancel = async ({ reason }: { reason: string }) => {
    if (!cancelTarget) return;
    try {
      await cancelOrder.mutateAsync({ id: cancelTarget.id, reason });
      void message.success(t("orders.cancelSuccess"));
      setCancelTarget(null);
    } catch {
      void message.error(t("orders.cancelFailed"));
    }
  };

  const onAction = async (id: string, status: string, order?: any): Promise<boolean> => {
    let note: string | undefined;
    if (status === "delivered") {
      // Same rule as the detail page: always confirm; a Lalamove rider who has not picked up needs a written reason.
      const picked = ["PICKED_UP", "COMPLETED"].includes(order?.rider?.lalamove_status ?? "");
      if (order?.rider?.lalamove_order_id && !picked) {
        const reason = window.prompt(t("orderDetail.deliverForcePrompt"));
        if (!reason || reason.trim().length < 3) return false;
        note = reason.trim();
      } else if (!(await confirmDialog({ title: t("orderDetail.deliverConfirmTitle"), description: t("orderDetail.deliverConfirmDesc"), danger: true }))) {
        return false;
      }
    }
    try {
      await updateStatus.mutateAsync({ id, status, ...(note ? { note } : {}) });
      return true;
    } catch {
      void message.error(t("orders.updateStatusFailed"));
      return false;
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await fetchAdminOrdersForExport({
        status: statusTab !== "all" ? statusTab : undefined,
        type: typeFilter !== "all" ? typeFilter : undefined,
        q: search.trim() || undefined,
        stallId,
        source,
        ...dateRange,
      }) as (Order & { type: string })[];
      if (!rows.length) return;
      const csvRows = rows.map((row) => {
        const cust = getOrderCustomerDisplay(row as Order & {
          customer_name?: string | null;
          customer_phone?: string | null;
          special_note?: string | null;
          customer?: { name?: string | null; phone?: string | null } | null;
        });
        return {
          OrderId: `#${row.order_code ?? row.id.slice(-8)}`,
          Type: row.type,
          Customer: cust.name ?? "",
          Phone: cust.phone ?? "",
          AmountRM: row.total,
          Status: row.status,
          CreatedAt: formatMytStamp(row.created_at),
        };
      });
      const headers = Object.keys(csvRows[0]);
      const csv = [
        headers.join(","),
        ...csvRows.map((r) => headers.map((h) => `"${String((r as Record<string, unknown>)[h] ?? "").replace(/"/g, '""')}"`).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${formatMytStamp(new Date()).slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const colHeader = (label: string) => <span className="text-xs font-extrabold uppercase tracking-[0.6px] text-[#534434]">{label}</span>;

  const columns: ColumnsType<Order> = [
    {
      title: colHeader(t("orders.colOrderId")),
      dataIndex: "order_code", key: "order_code", width: 150,
      render: (v, row) => {
        const ord = row as Order & { type: string };
        return (
          <div>
            <div className="admin-orders-code admin-data-code text-sm font-extrabold tracking-[-0.2px] text-[#191c1d]">#{v ?? row.id.slice(-8)}</div>
            <div className="admin-orders-channel mt-1 flex items-center gap-1">
              {ord.type === "delivery"
                ? <CarOutlined className="text-[11px] text-[#534434]" />
                : <ShoppingOutlined className="text-[11px] text-[#534434]" />}
              <span className="text-[10px] font-extrabold uppercase tracking-[-0.3px] text-[#534434]">{t(`orders.type.${ord.type}`, { defaultValue: ord.type })}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: colHeader(t("orders.colCustomer")),
      key: "customer", width: 170,
      render: (_, row) => {
        const cust = getOrderCustomerDisplay(row as Order & {
          customer_name?: string | null;
          customer_phone?: string | null;
          special_note?: string | null;
          customer?: { name?: string | null; phone?: string | null } | null;
        });
        return (
          <div>
            <div className="admin-orders-customer admin-data-customer text-sm font-semibold leading-5 text-[#191c1d]">{cust.name ?? cust.phone ?? "—"}</div>
            {cust.phone && cust.name && <div className="admin-orders-secondary admin-data-secondary text-xs leading-4 text-[#534434]">{cust.phone}</div>}
          </div>
        );
      },
    },
    {
      title: colHeader(t("orders.colAmount")),
      dataIndex: "total", key: "total", width: 120,
      render: (v) => <span className="admin-orders-amount admin-data-amount text-sm font-extrabold text-[#191c1d]">RM {rm(v)}</span>,
    },
    {
      title: colHeader(t("orders.colOrderTime")),
      dataIndex: "created_at", key: "created_at", width: 140,
      render: (v) => (
          <div>
          <div className="admin-orders-time admin-data-time text-sm font-semibold leading-5 text-[#191c1d]">{formatOrderTime(v, i18n.language)}</div>
          <div className="admin-orders-secondary admin-data-secondary admin-orders-date text-xs leading-4 text-[#534434]">{formatOrderDate(v, i18n.language)}</div>
        </div>
      ),
    },
    {
      title: colHeader(t("orders.colStatus")),
      dataIndex: "status", key: "status", width: 170,
      render: (v, row) => {
        const rowTxn = (row as unknown as { transactions?: Array<{ status?: string | null; method?: string | null }> }).transactions?.[0];
        const showUnpaid = rowTxn?.method !== "cash" && (rowTxn?.status === "pending" || rowTxn?.status === "failed");
        return (
          <div className="flex flex-col gap-1">
            <OrderStatusPill status={v} />
            {showUnpaid && (
              <span className={`inline-flex w-fit items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase text-white ${rowTxn!.status === "failed" ? "bg-[#dc2626]" : "bg-[#d97706]"}`}>
                {rowTxn!.status === "failed" ? t("orders.drawerPaymentFailedBadge") : t("orders.drawerUnpaidBadge")}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: colHeader(t("orders.colAction")),
      key: "actions", width: 170, fixed: "right" as const,
      render: (_, row) => {
        const ord = row as Order & { type: string };
        const dropdownItems = [
          { key: "details", label: <span className="text-[13px] font-semibold uppercase tracking-[0.4px]">{t("orders.menuOverview")}</span>, onClick: () => setSelectedId(row.id) },
          { key: "overview", label: <span className="text-[13px] font-semibold uppercase tracking-[0.4px]">{t("orders.menuFullDetails")}</span>, onClick: () => navigate(`/orders/${row.id}`) },
          !CANCEL_HIDDEN_ORDER_STATUSES.has(row.status)
            ? { key: "cancel", label: <span className="text-[13px] font-semibold uppercase tracking-[0.4px] text-red-600">{t("orders.menuCancelOrder")}</span>, disabled: TERMINAL_ORDER_STATUSES.has(row.status), onClick: () => { setCancelTarget(row); } }
            : null,
        ].filter(Boolean);

        return (
          <div className="admin-orders-actions flex min-w-[140px] items-center gap-3">
            <ActionButton order={ord} onAction={onAction} />
            <Dropdown
              trigger={["click"]}
              menu={{
                items: dropdownItems,
              }}
            >
              <button type="button" className="admin-orders-more admin-data-more inline-flex cursor-pointer items-center justify-center border-none bg-transparent px-2 py-1 text-slate-500">
                <EllipsisOutlined className="text-lg" />
              </button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <AdminShell>
      <div className={`admin-orders-page admin-list-page ${ORDERS_PAGE_CLASS}`}>
        <div className="mx-auto">
          <div ref={filterRef} className={`admin-orders-filter admin-fixed-filter ${ORDERS_FILTER_CLASS}`}>
            <div className="mx-auto">
              <div className="admin-orders-toolbar mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="admin-orders-heading flex min-w-[220px] flex-col items-start gap-[9px]">
                  <div className="admin-page-title m-0 text-2xl font-black leading-8 tracking-[-0.6px] text-[#191c1d]">{t("orders.listHeading")}</div>
                  <div className="admin-orders-live flex w-full items-center gap-2">
                    <span className="inline-block size-2 shrink-0 rounded-full bg-[#006c49]" />
                    <span className="text-xs font-medium leading-4 text-[#534434]">
                      {stats ? t("orders.liveFeedActive", { count: activeOrders }) : t("common.loading", "Loading…")}
                    </span>
                  </div>
                  {customerId && (
                    <button
                      type="button"
                      onClick={() => { setSearchParams((prev) => { prev.delete("customerId"); return prev; }); setPage(1); }}
                      className="admin-orders-customer-chip mt-1 inline-flex items-center gap-1.5 self-start rounded-full border border-[#ea580c]/30 bg-[#ea580c]/10 px-3 py-1 text-xs font-semibold text-[#c2410c]"
                    >
                      {t("orders.filteredByCustomer")}
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>

                <div className="admin-orders-tools flex w-full flex-wrap items-center justify-end gap-3 sm:w-auto">
                  <div className="admin-orders-search relative w-full max-w-[420px] min-w-[260px] sm:w-[360px] xl:w-[420px]">
                    <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#867461]" />
                    <input
                      type="text"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder={t("orders.searchPlaceholder")}
                      className="admin-orders-search-input h-11 w-full rounded-lg border border-[#e7e1db] bg-white pl-11 pr-11 text-sm font-semibold text-[#191c1d] outline-none shadow-[0_12px_28px_rgba(25,28,29,0.06)] placeholder:text-[#867461] focus:border-[#ea580c] focus:ring-4 focus:ring-[#ea580c]/10"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        className="admin-orders-search-clear absolute right-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border-none bg-[#f3f4f5] text-[#534434] transition hover:bg-[#ffedd5] hover:text-[#ea580c]"
                        aria-label="Clear search"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={exporting}
                    onClick={() => void handleExport()}
                    className="admin-orders-export h-11 shrink-0 rounded-lg font-bold"
                  >
                    {t("common.exportCsv")}
                  </Button>
                </div>
              </div>

              <div className="admin-orders-statusbar flex items-center gap-3 rounded-lg border border-[#eee8e2] bg-white/85 p-2 shadow-[0_12px_30px_rgba(25,28,29,0.05)] backdrop-blur max-lg:flex-col max-lg:items-stretch">
                <div className="admin-orders-tabs flex min-w-0 flex-1 items-start gap-1 overflow-x-auto rounded-lg bg-[#f3f4f5] p-1">
                  {STATUS_TABS.map((tab) => {
                    const active = statusTab === tab.key;
                    return (
                      <button
                        type="button"
                        key={tab.key}
                        onClick={() => { setStatusTab(tab.key); setPage(1); }}
                        className={`admin-orders-tab min-h-14 whitespace-nowrap rounded-lg border-none px-4 text-sm leading-5 ${
                          tab.key === "out_for_delivery" ? "py-2" : "py-[18px]"
                        } ${active ? "admin-orders-tab-active bg-white font-extrabold text-[#ea580c] shadow-[0_1px_1px_rgba(0,0,0,0.05)]" : "bg-transparent font-medium text-[#534434]"}`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
                <div className="admin-orders-filter-divider hidden h-8 w-px shrink-0 bg-[#e7e1db] lg:block" />
                <div className="admin-orders-type-filter flex shrink-0 items-center justify-between gap-2 rounded-lg bg-[#fff7ed] px-2 py-1 max-lg:w-full">
                  <span className="pl-1 text-[10px] font-black uppercase leading-4 tracking-[0.6px] text-[#a14000]">{t("orders.filterType")}</span>
                  <div className="admin-orders-type-options flex items-start gap-1 rounded-lg bg-[#f3f4f5] p-1">
                    {(["all", "pickup", "delivery"] as const).map((tp) => {
                      const active = typeFilter === tp;
                      const disabled = source === "walkin" && tp === "delivery";
                      return (
                        <button
                          type="button"
                          key={tp}
                          disabled={disabled}
                          title={disabled ? t("orders.typeDisabledWalkinHint") : undefined}
                          onClick={() => { setTypeFilter(tp); setPage(1); }}
                          className={`admin-orders-type-button min-h-7 whitespace-nowrap rounded-lg border-none px-3 py-1.5 text-xs leading-4 ${
                            disabled
                              ? "cursor-not-allowed bg-transparent font-medium text-[#c4bab0]"
                              : active
                                ? "admin-orders-type-active bg-white font-extrabold text-[#191c1d] shadow-[0_1px_1px_rgba(0,0,0,0.05)]"
                                : "bg-transparent font-medium text-[#534434]"
                          }`}
                        >
                          {tp === "all" ? t("orders.filterAll") : t(`orders.type.${tp}`, { defaultValue: tp })}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="admin-orders-filter-bottom mt-2 flex items-center gap-3 rounded-lg border border-[#eee8e2] bg-white/85 p-2 shadow-[0_12px_30px_rgba(25,28,29,0.05)] backdrop-blur max-lg:flex-col max-lg:items-stretch">
                <div className="admin-orders-filter-fields grid min-w-0 flex-1 grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2">
                  <Select
                    value={datePreset}
                    onChange={(v) => { setDatePreset(v as string); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={[
                      { value: "all", label: t("transactions.datePresetAll") },
                      { value: "7d", label: t("transactions.datePreset7d") },
                      { value: "30d", label: t("transactions.datePreset30d") },
                      { value: "90d", label: t("transactions.datePreset90d") },
                    ]}
                  />
                  <Select
                    placeholder={t("orders.filterAllStalls")}
                    value={stallId ?? ""}
                    onChange={(v) => { setStallId((v as string) || undefined); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={[
                      { value: "", label: t("orders.filterAllStalls") },
                      ...stalls.map((s: { id: string; name: string }) => ({ value: s.id, label: s.name })),
                    ]}
                  />
                  <Select
                    placeholder={t("transactions.filterAllSources")}
                    allowClear
                    value={source}
                    onChange={(v) => { setSource(v as string | undefined); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={[
                      { value: "walkin", label: t("transactions.sourceWalkin") },
                      { value: "customer", label: t("transactions.sourceCustomerApp") },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Spacer, NOT part of the scroll container below — pushes the table's own
              bounding box to start below the fixed filter panel. Scrolling *inside*
              a box that also contains this padding would scroll the padding away too,
              letting rows travel back up underneath the fixed panel (found via #227). */}
          <div style={{ height: filterHeight ? filterHeight + 24 : 190 }} />
          <div
            className="admin-orders-table-panel admin-data-table-panel border-none bg-transparent shadow-none"
            style={{
              maxHeight: filterHeight ? `calc(100dvh - 64px - ${filterHeight + 24}px - var(--orders-page-top) - var(--orders-page-bottom))` : undefined,
              overflowY: filterHeight ? "auto" : "visible",
            }}
          >
          <Table<Order>
            columns={columns}
            dataSource={data?.data ?? []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: page,
              total: data?.meta.total ?? 0,
              pageSize: 10,
              onChange: (p) => setPage(p),
            }}
            size="middle"
            scroll={{ x: 1000 }}
            rowClassName={(_, index) => orderRowClass(index)}
          />
          </div>
        </div>

      </div>

      {/* Order Detail Drawer */}
      <Drawer
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        width={420}
        closeIcon={false}
        styles={{ body: { padding: 0 }, header: { display: "none" } }}
      >
        {orderDetail?.data && (() => {
          const od = orderDetail.data;
          const odExt = od as unknown as {
            order_code?: string;
            customer_name?: string | null;
            customer_phone?: string | null;
            customer?: { name?: string; phone?: string } | null;
            payment_method?: string;
            transactions?: Array<{ method?: string | null; status?: string | null; billplz_paid?: boolean | null; paid_at?: string | null }>;
            cancel_reason?: string | null;
            voucher_code?: string;
            special_note?: string | null;
            est_ready_at?: string | null;
            ready_at?: string | null;
            completed_at?: string | null;
            delivered_at?: string | null;
            items?: OrderItem[];
            sst_rate?: number;
          };
          const drawerCustomer = getOrderCustomerDisplay(od as Order & {
            customer_name?: string | null;
            customer_phone?: string | null;
            special_note?: string | null;
            customer?: { name?: string | null; phone?: string | null } | null;
          });
          const custName = drawerCustomer.name ?? drawerCustomer.phone ?? "—";
          const custPhone = drawerCustomer.phone;
          const initials = custName === "—" ? "?" : custName.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
          const itemCount = odExt.items?.length ?? 0;
          const ord2 = od as Order & { type: string };
          const paidTxn = odExt.transactions?.find((txn) => txn.status === "success" || txn.billplz_paid || txn.paid_at) ?? null;
          const txn = paidTxn ?? odExt.transactions?.[0] ?? null;
          const paymentMethod = txn?.method ?? odExt.payment_method ?? "—";
          const showReadyTimeRow = READY_OR_LATER_ORDER_STATUSES.has(od.status);
          const drawerTimeValue = od.type === "pickup"
            ? (odExt.completed_at ?? odExt.ready_at ?? odExt.est_ready_at ?? null)
            : (odExt.delivered_at ?? odExt.ready_at ?? odExt.est_ready_at ?? null);

          const drawerAction = getOrderStatusAction(ord2);

          return (
            <div className="flex h-full flex-col">
              {/* Scrollable body */}
              <div className={`flex-1 overflow-y-auto ${!TERMINAL_ORDER_STATUSES.has(od.status) ? "pb-[100px]" : "pb-7"}`}>

                {/* Close button */}
                <button
                  onClick={() => setSelectedId(null)}
                  className="absolute right-5 top-5 z-10 cursor-pointer border-none bg-transparent text-lg leading-none text-[#534434]"
                >
                  ✕
                </button>

                {/* Order code + status pill */}
                <div className="text-[26px] font-extrabold text-[#191c1d] mb-3">#{odExt.order_code ?? od.id.slice(-8)}</div>

                {/* Timestamp */}
                <div className="flex items-center mb-5 gap-3">
                  <div className="text-[13px] text-[#737373]">
                    {formatOrderTime(od.created_at, i18n.language)} • {formatOrderDate(od.created_at, i18n.language)}
                  </div>
                  <OrderStatusPill status={od.status} />
                </div>

                {/* Channel + Paid tags */}
                <div className="mb-7 flex gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#d1d5db] bg-white px-3.5 py-[5px] text-[13px] text-[#191c1d]">
                    {od.type === "delivery" ? <CarOutlined className="text-[13px]" /> : <ShoppingOutlined className="text-[13px]" />}
                    {od.type === "delivery" ? t("orders.drawerChannelDelivery") : t("orders.drawerChannelPickup")}
                  </span>
                  {paidTxn && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#16a34a] bg-white px-3.5 py-[5px] text-[13px] font-semibold text-[#16a34a]">
                      <CheckOutlined/> {t("orders.drawerPaid")}
                    </span>
                  )}
                  {!paidTxn && txn?.method !== "cash" && (txn?.status === "pending" || txn?.status === "failed") && (
                    <span className={`inline-flex items-center gap-1 rounded-full border bg-white px-3.5 py-[5px] text-[13px] font-semibold ${txn.status === "failed" ? "border-[#dc2626] text-[#dc2626]" : "border-[#d97706] text-[#d97706]"}`}>
                      {txn.status === "failed" ? t("orders.drawerPaymentFailed") : t("orders.drawerUnpaid")}
                    </span>
                  )}
                </div>

                {od.status === "cancelled" && (
                  <div className="mb-5 rounded-[10px] border border-[#fecaca] bg-[#fef2f2] px-4 py-3">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#dc2626]">{t("orders.drawerCancelReason")}</div>
                    <div className="text-[13px] leading-5 text-[#7f1d1d]">{cancelReasonLabel(odExt.cancel_reason || "others", i18n.language)}</div>
                  </div>
                )}

                {/* CUSTOMER DETAILS */}
                <div className={showReadyTimeRow ? "mb-3" : "mb-5"}>
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#737373]">{t("orders.drawerCustomerDetails")}</div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#e5e7eb] text-[15px] font-bold text-[#534434]">
                        {initials}
                      </div>
                      <div>
                        <div className="text-[15px] font-bold text-[#191c1d]">{custName}</div>
                        {custPhone && drawerCustomer.name && <div className="mt-0.5 text-[13px] text-[#737373]">{custPhone}</div>}
                      </div>
                    </div>
                    {custPhone && (
                      <a
                        href={`tel:${custPhone}`}
                        className="flex size-9 shrink-0 items-center justify-center rounded-full border border-[#e5e7eb] text-[#534434] no-underline"
                      >
                        <PhoneOutlined className="text-base" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Pickup / delivery time row */}
                {showReadyTimeRow && (
                  <div className="mb-7 flex items-center justify-between rounded-lg bg-[#f8f9fa] px-3.5 py-2.5">
                    <span className="text-[13px] text-[#534434]">{od.type === "delivery" ? t("orders.drawerDeliveryTime") : t("orders.drawerPickupTime")}</span>
                    <span className="text-[13px] font-bold text-[#191c1d]">{drawerTimeValue ? formatOrderTime(drawerTimeValue, i18n.language) : t("orders.drawerAsap")}</span>
                  </div>
                )}

                {/* RIDER (delivery) */}
                {od.type === "delivery" && odExt.rider && (
                  <div className="mb-5 rounded-lg bg-[#f8f9fa] px-3.5 py-3 text-[13px] text-[#534434]">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#737373]">{t("orders.drawerRider")}</div>
                    {odExt.rider.name && odExt.rider.lalamove_status !== "ASSIGNING_DRIVER" && (
                      <div className="flex justify-between"><span className="font-bold text-[#191c1d]">{odExt.rider.name}</span>
                        {odExt.rider.phone && <a href={`tel:${odExt.rider.phone}`} className="text-[#534434]">{odExt.rider.phone}</a>}</div>
                    )}
                    {odExt.rider.lalamove_order_id && (
                      <div className="mt-1 flex justify-between"><span>{t("orders.drawerLalamoveId")}</span><span className="font-mono text-xs">{odExt.rider.lalamove_order_id}</span></div>
                    )}
                    {odExt.rider.lalamove_order_id && (
                      <div className="mt-1 flex justify-between"><span>{t("orders.drawerProgress")}</span><span className="font-bold text-[#191c1d]">{t(lalamoveStatusKey(odExt.rider.lalamove_status))}</span></div>
                    )}
                    {odExt.rider.share_link && (
                      <a href={odExt.rider.share_link} target="_blank" rel="noopener noreferrer" className="mt-1 block font-bold text-[#ea580c]">{t("orders.drawerTrack")} ↗</a>
                    )}
                  </div>
                )}

                {/* ORDER ITEMS */}
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.08em] text-[#737373]">{t("orders.drawerOrderItemsCount", { count: itemCount })}</div>
                {odExt.items?.map((item, i) => {
                  const extrasTotal = item.options.reduce((s, o) => s + parseFloat(o.extra_charge), 0);
                  const extrasMarkup = parseFloat((item as { options_markup_amount?: string }).options_markup_amount ?? "0") + parseFloat((item as { options_sst_amount?: string }).options_sst_amount ?? "0");
                  const unitPrice = parseFloat(item.price) + extrasTotal + extrasMarkup;
                  const imageUrl = (item as { image_url?: string | null; menu_item?: { image_url?: string | null } }).image_url ?? (item as { menu_item?: { image_url?: string | null } }).menu_item?.image_url;
                  return (
                    <div key={i} className="mb-5 flex items-start gap-3">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.name}
                          className="size-[52px] shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex size-[52px] shrink-0 items-center justify-center rounded-lg bg-[#f3f4f6] text-lg font-bold text-[#737373]">
                          {item.name.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-bold leading-[1.35] text-[#191c1d]">{item.name}</div>
                        {(item.options.length > 0 || item.note) && (
                          <div className="mt-[3px] text-xs text-[#737373]">
                            {[item.options.map((o) => (o.group ? `${o.group}: ${o.label}` : o.label)).join(", "), item.note].filter(Boolean).join(" • ")}
                          </div>
                        )}
                        <div className="mt-1 text-[13px] font-medium text-[#534434]">RM {unitPrice.toFixed(2)}</div>
                      </div>
                      <div className="flex h-8 min-w-8 shrink-0 items-center justify-center rounded-full bg-[#ffddb8] text-xs font-bold text-[#ea580c]">
                        x{item.qty}
                      </div>
                    </div>
                  );
                })}

                {/* Price breakdown */}
                <div className="mt-1 border-t border-[#f3f4f6] pt-4">
                  {([
                    { label: t("orders.drawerSubtotal"), value: `RM ${rm(od.subtotal)}` },
                    parseFloat(od.voucher_discount) > 0 ? { label: t("orders.discountWithCode", { code: odExt.voucher_code ?? t("orders.voucherFallback") }), value: `-RM ${rm(od.voucher_discount)}`, red: true } : null,
                    parseFloat(od.sst_amount) > 0 && !(od as { sst_included?: boolean }).sst_included ? { label: t("orders.sstWithRate", { rate: odExt.sst_rate != null ? (Number(odExt.sst_rate) * 100).toFixed(1).replace(/\.0$/, "") : 8 }), value: `RM ${rm(od.sst_amount)}` } : null,
                    parseFloat(od.delivery_fee) > 0 ? { label: t("orders.drawerDelivery"), value: `RM ${rm(od.delivery_fee)}` } : null,
                  ] as Array<{ label: string; value: string; red?: boolean } | null>).filter(Boolean).map((row, i) => row && (
                    <div key={i} className="mb-2.5 flex justify-between">
                      <span className="text-[13px] text-[#534434]">{row.label}</span>
                      <span className={`text-[13px] ${row.red ? "text-red-600" : "text-[#191c1d]"}`}>{row.value}</span>
                    </div>
                  ))}
                </div>

                {/* Total Amount */}
                <div className="mt-1 flex items-center justify-between border-t-2 border-[#f3f4f6] py-3.5">
                  <span className="text-base font-extrabold text-[#191c1d]">{t("orders.drawerTotalAmount")}</span>
                  <span className="text-[22px] font-extrabold text-[#ea580c]">RM {rm(od.total)}</span>
                </div>

                {/* Payment method */}
                <div className="mt-1 rounded-[10px] bg-[#f8f9fa] px-4 py-3">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[#737373]">{t("orders.drawerPaymentMethod")}</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold uppercase text-[#191c1d]">{paymentMethod}</span>
                    {paidTxn && (
                      <span className="rounded bg-[#006c49] px-2.5 py-1.5 text-[11px] font-bold text-white leading-none inline-flex items-center">{t("orders.drawerPaidBadge")}</span>
                    )}
                    {!paidTxn && txn?.method !== "cash" && (txn?.status === "pending" || txn?.status === "failed") && (
                      <span className={`rounded px-2.5 py-1.5 text-[11px] font-bold text-white leading-none inline-flex items-center ${txn.status === "failed" ? "bg-[#dc2626]" : "bg-[#d97706]"}`}>
                        {txn.status === "failed" ? t("orders.drawerPaymentFailedBadge") : t("orders.drawerUnpaidBadge")}
                      </span>
                    )}
                  </div>
                </div>

              </div>

              {/* Footer actions */}
              {!TERMINAL_ORDER_STATUSES.has(od.status) && (
                <div className="absolute inset-x-0 bottom-0 flex gap-3 border-t border-[#e5e7eb] bg-white px-6 py-4">
                  <Button
                    className="h-12 flex-1 rounded-[10px] text-sm font-semibold"
                    onClick={() => { setSelectedId(null); setCancelTarget(od); }}
                  >
                    {t("orders.cancelOrderAction")}
                  </Button>
                  {drawerAction && (
                    <button
                      type="button"
                      className={`h-12 flex-[1.5] rounded-[10px] border-none text-sm font-bold text-white ${
                        drawerAction.actionKey === "delivered" ? "bg-[#006c49]" : "bg-[#ea580c]"
                      }`}
                      onClick={async () => { if (await onAction(od.id, drawerAction.nextStatus, od)) setSelectedId(null); }}
                    >
                      {t(ORDER_ACTION_LABEL_KEY[drawerAction.actionKey])}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </Drawer>

      <OrderCancelModal
        open={!!cancelTarget}
        orderCode={cancelTarget?.order_code}
        onCancel={() => { setCancelTarget(null); }}
        onConfirm={onCancel}
        loading={cancelOrder.isPending}
      />
    </AdminShell>
  );
}
